/* eslint-disable import/no-cycle, @typescript-eslint/no-explicit-any, no-param-reassign */
import { LogManager } from '@src/utility/Logger';

import { extractErrorInfo } from './error';

import type {
  AlbumQueryResult,
  AlbumStory,
  DashboardStory,
  DatoAlbumQueryResult,
  DatoShowcase,
  DatoStoryQueryResult,
  Showcase,
  Story,
  StoryQueryResult,
  StoryVideo,
  Transcription,
} from '../types.ts/story';

const logger = LogManager.getLogger('utility:story');

const THUMBNAILS_TIMECODE_LENGTH = 6;

/** Maximum number of stories per batch request to api/stories/populate */
const POPULATE_BATCH_SIZE = 50;

/** Server-enforced MAX_BATCH_SIZE of api/stories/sign — larger requests 400. */
const SIGN_BATCH_SIZE = 100;

export type StorySignedUrls = {
  thumbnailUrl: string;
  duration: number;
};

async function fetchSignedUrlsBatch(
  storyIds: string[],
  signal?: AbortSignal,
): Promise<Record<string, StorySignedUrls>> {
  const url = `${process.env.REACT_APP_API_URL}/api/stories/sign`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storyIds }),
      signal,
    });
    if (response.ok) {
      return (await response.json()).data;
    }
    logger.error('Failed to fetch signed URLs', {
      storyCount: storyIds.length,
      status: response.status,
    });
    return {};
  } catch (error) {
    if (error instanceof Error && error.name !== 'AbortError') {
      logger.error('Error fetching signed URLs', { error: error.message });
    }
    return {};
  }
}

/**
 * Fetch signed CloudFront thumbnail URLs for stories.
 * This is a lightweight alternative to the full populate endpoint —
 * it only generates signed URLs without listing S3 objects or reading metadata.
 * Requests are chunked to the endpoint's batch limit and run in parallel, so
 * any number of stories is safe (a single oversized request would 400).
 */
export async function fetchSignedUrls(
  storyIds: string[],
  signal?: AbortSignal,
): Promise<Record<string, StorySignedUrls>> {
  if (!storyIds.length) return {};

  const batches: string[][] = [];
  for (let i = 0; i < storyIds.length; i += SIGN_BATCH_SIZE) {
    batches.push(storyIds.slice(i, i + SIGN_BATCH_SIZE));
  }

  const batchResults = await Promise.all(
    batches.map(batch => fetchSignedUrlsBatch(batch, signal)),
  );

  return batchResults.reduce(
    (merged, batchResult) => ({ ...merged, ...batchResult }),
    {},
  );
}

export const getAiDescriptionResponse = (story: AlbumStory) =>
  story?.aiResponse?.responses?.find(
    response => response.title === 'Description',
  );

export const updateStoryShowcase = (
  storyData: Story,
  album: Showcase,
): void => {
  storyData.primaryShowcase = album;
  const showcaseIdx = storyData._allReferencingShowcases?.findIndex(
    s => s.id === album.id,
  );
  if (showcaseIdx > -1) {
    storyData._allReferencingShowcases =
      storyData._allReferencingShowcases?.map(s =>
        s.id === album.id ? album : s,
      );
  } else {
    storyData._allReferencingShowcases =
      storyData._allReferencingShowcases?.concat(album) || [];
  }
};

/**
 * Fetches a single batch of stories from the populate endpoint.
 * Internal helper - use requestMissingFieldsBatch for public API.
 */
async function fetchPopulateBatch(
  storyIds: string[],
  fields: ('originalVideo' | 'transcription')[],
  signal?: AbortSignal,
): Promise<
  Record<string, { originalVideo?: StoryVideo; transcription?: Transcription }>
> {
  const url = `${process.env.REACT_APP_API_URL}/api/stories/populate`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ storyIds, fields }),
    signal,
  });
  if (response.ok) {
    return (await response.json()).data;
  }
  logger.error('Failed to batch fetch cloudfront links', {
    storyCount: storyIds.length,
  });
  return {};
}

export async function requestMissingFieldsBatch(
  storyIds: string[],
  fields: ('originalVideo' | 'transcription')[],
  signal?: AbortSignal,
): Promise<
  Record<string, { originalVideo?: StoryVideo; transcription?: Transcription }>
> {
  if (!storyIds.length) return {};

  // Split into batches of POPULATE_BATCH_SIZE to respect API limits
  const batches: string[][] = [];
  for (let i = 0; i < storyIds.length; i += POPULATE_BATCH_SIZE) {
    batches.push(storyIds.slice(i, i + POPULATE_BATCH_SIZE));
  }

  // Fetch all batches in parallel
  const batchResults = await Promise.all(
    batches.map(batch => fetchPopulateBatch(batch, fields, signal)),
  );

  // Merge all batch results into a single record
  return batchResults.reduce(
    (merged, batchResult) => ({ ...merged, ...batchResult }),
    {},
  );
}

export async function requestMissingFields(
  storyId: string,
  fields: ('originalVideo' | 'transcription')[],
  signal?: AbortSignal,
): Promise<{ originalVideo?: StoryVideo; transcription?: Transcription }> {
  const url = `${process.env.REACT_APP_API_URL}/api/story/${storyId}/populate`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ fields }),
    signal,
  });
  if (response.ok) {
    return (await response.json()).data;
  }
  logger.error('Failed to fetch cloudfront links', { storyId });
  return {};
}

export async function populateShowcaseStoriesWithOriginalVideo(
  showcase: DatoShowcase,
): Promise<Showcase> {
  // One request per 50 stories rather than one per story: a cold load of a
  // 77-story workspace used to fire 77 parallel populate calls here.
  const populated = await requestMissingFieldsBatch(
    showcase.stories.map(story => story.id),
    ['originalVideo'],
  );
  // A failed batch returns none of its ids; fetch those AWS stories one by one
  // so one bad batch can't blank up to 50 stories' videos.
  const missing = showcase.stories.filter(
    story => story.useAws && !(story.id in populated),
  );
  const retried = await Promise.all(
    missing.map(
      async story =>
        [
          story.id,
          await requestMissingFields(story.id, ['originalVideo']),
        ] as const,
    ),
  );
  for (const [id, fields] of retried) populated[id] = fields;

  const storiesWithOriginalVideo: Showcase['stories'] = showcase.stories.map(
    story => {
      const selfHostedVideo = populated[story.id]?.originalVideo;
      return {
        ...story,
        originalVideo: story.useAws ? selfHostedVideo! : story.originalVideo,
        // Temporary added to support both self hosted and dato hosted videos
        selfHostedVideo,
        datoHostedVideo: story.originalVideo,
      };
    },
  );
  return {
    ...showcase,
    stories: storiesWithOriginalVideo,
  };
}

export async function populateAlbumQueryStoriesWithOriginalVideo(
  album: DatoAlbumQueryResult,
): Promise<AlbumQueryResult> {
  const storiesWithOriginalVideo: AlbumQueryResult['stories'] =
    await Promise.all(
      album.stories.map(async story => {
        const selfHostedVideo = (
          await requestMissingFields(story.id, ['originalVideo'])
        ).originalVideo;
        return {
          ...story,
          originalVideo: story.useAws ? selfHostedVideo! : story.originalVideo,
          // Temporary added to support both self hosted and dato hosted videos
          selfHostedVideo,
          datoHostedVideo: story.originalVideo,
        };
      }),
    );
  return {
    ...album,
    stories: storiesWithOriginalVideo,
  };
}

export async function populateStoryQueryWithMissingFields(
  story: DatoStoryQueryResult['story'],
  signal?: AbortSignal,
): Promise<StoryQueryResult['story']> {
  if (!story) {
    return story;
  }

  const {
    originalVideo: selfHostedVideo,
    transcription: selfHostedTranscription,
  } = await requestMissingFields(
    story.id,
    ['originalVideo', 'transcription'],
    signal,
  );
  return {
    ...story,
    transcription: story.useAws
      ? selfHostedTranscription!
      : story.transcription,
    originalVideo: story.useAws ? selfHostedVideo! : story.originalVideo,
    // Temporary added to support both self hosted and dato hosted videos
    selfHostedVideo,
    datoHostedVideo: story.originalVideo,
  };
}

export function getSelfHostedThumbnailUrl(
  originalVideo: StoryVideo,
  timecode?: number,
): string {
  if (!originalVideo?.video) return '';
  const { thumbnailUrl } = originalVideo.video;
  if (!timecode) return thumbnailUrl;
  const withTimeCode = thumbnailUrl.replace(
    'thumbnail',
    `thumbnails/${Math.round(timecode)
      .toString()
      .padStart(THUMBNAILS_TIMECODE_LENGTH, '0')}`,
  );
  return withTimeCode;
}

/**
 * A single frame from a Mux asset, as an image URL.
 *
 * Sizing defaults to a 187px height. Pass `width` and/or `height` to bound the
 * request instead — with the default `fit_mode=preserve`, Mux fits the frame
 * inside that box and keeps its aspect ratio, so passing both is how a caller
 * asks for "at most this big" without knowing whether the clip is 16:9 or 9:16.
 */
export function getMuxHostedThumbnailUrl(
  muxPlaybackId: string,
  timecode?: number,
  options?: {
    width?: number;
    height?: number;
    fitMode?: 'preserve' | 'crop' | 'smartcrop' | 'pad';
  },
) {
  const params = new URLSearchParams();
  if (options?.width != null) params.set('width', String(options.width));
  if (options?.height != null) params.set('height', String(options.height));
  if (options?.width == null && options?.height == null) {
    params.set('height', '187');
  }
  if (options?.fitMode) params.set('fit_mode', options.fitMode);
  // `!= null`, not truthiness: `time=0` is a real request (the clip's first
  // frame) and a falsy check silently dropped it, falling back to Mux's own
  // default frame.
  if (timecode != null) params.set('time', String(timecode));
  return `https://image.mux.com/${muxPlaybackId}/thumbnail.jpg?${params}`;
}

/**
 * A frame from the story's **source footage** — the customer's upload, before
 * anything Arbor renders over it.
 *
 * Use this wherever a frame is meant to stand for "the real footage". The
 * general chain in `getThumbnailUrlAndAspectRatio` prefers a Video record's
 * own `thumbnail` / `videoFilePrimary`, and both of those are artifacts of a
 * *published render* — captions, lower thirds and intro cards are flattened
 * into those pixels. That is right for a clip poster (it shows what was
 * published) and wrong for anything previewing an overlay, which would then
 * show its own subject twice.
 *
 * `mediaStartTime` omitted resolves the video's stored poster frame. Returns
 * undefined when no source frame can be resolved, leaving the caller to fall
 * back (typically to a packaged backdrop).
 */
export function sourceFrameThumbnailUrl(
  story: Story | null | undefined,
  mediaStartTime?: number,
  options?: { width?: number; height?: number },
): string | undefined {
  const originalVideo = story?.originalVideo;
  if (!originalVideo) return undefined;

  if (story?.useAws) {
    return (
      getSelfHostedThumbnailUrl(originalVideo, mediaStartTime) || undefined
    );
  }

  const playbackId = originalVideo.video?.muxPlaybackId;
  if (!playbackId) {
    // No Mux asset to seek in; the stored poster is still source footage.
    return originalVideo.video?.thumbnailUrl || undefined;
  }

  return getMuxHostedThumbnailUrl(
    playbackId,
    mediaStartTime == null ? undefined : Math.floor(mediaStartTime),
    options,
  );
}

/**
 * Represents a clip theme/topic extracted from clipThemesJson.
 */
export type ClipTheme = {
  title: string;
  speakerLabels?: string[];
};

/**
 * Parses the clipThemesJson field from a story into structured ClipTheme objects.
 * Handles both string (JSON) and already-parsed object/array formats.
 * Deduplicates by title (case-sensitive, trimmed).
 *
 * @param raw - The raw clipThemesJson value (string, array, or object with themes property)
 * @returns Array of ClipTheme objects with unique titles
 */
export function parseClipThemesJson(raw: unknown): ClipTheme[] {
  try {
    if (!raw) return [];
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const themesArray: unknown[] = Array.isArray(parsed)
      ? parsed
      : Array.isArray((parsed as { themes?: unknown[] })?.themes)
        ? (parsed as { themes: unknown[] }).themes
        : [];

    const result: ClipTheme[] = [];
    const seenTitles = new Set<string>();

    for (const item of themesArray) {
      const title =
        typeof item === 'string'
          ? item
          : (item as { title?: string } | null)?.title;

      if (!title || typeof title !== 'string' || !title.trim()) continue;

      const normTitle = title.trim();
      if (seenTitles.has(normTitle)) continue;
      seenTitles.add(normTitle);

      const speakerLabels: string[] | undefined =
        typeof item === 'object' && item !== null
          ? (item as { speakerLabels?: string[]; speaker_labels?: string[] })
              ?.speakerLabels ||
            (item as { speakerLabels?: string[]; speaker_labels?: string[] })
              ?.speaker_labels ||
            undefined
          : undefined;

      result.push({ title: normTitle, speakerLabels });
    }

    return result;
  } catch (e) {
    logger.warn('Failed to parse clipThemesJson', { error: e });
    return [];
  }
}

/**
 * Parses clipThemesJson and returns just the theme titles as strings.
 * Convenience wrapper around parseClipThemesJson for cases where only titles are needed.
 *
 * @param raw - The raw clipThemesJson value
 * @returns Array of unique theme title strings
 */
export function parseClipThemeTitles(raw: unknown): string[] {
  return parseClipThemesJson(raw).map(theme => theme.title);
}

export const TOPIC_PARAGRAPH_MAPPING_VERSION = 1 as const;

/**
 * Cached mapping of topic title -> paragraph_id[] produced by TopicTaggingService.
 * Persisted inside story.aiResponse so re-opening the tab reuses prior work.
 */
export type TopicParagraphMapping = {
  version: typeof TOPIC_PARAGRAPH_MAPPING_VERSION;
  generatedAt: number;
  topicsHash: string;
  transcriptHash: string;
  byTopic: Record<string, string[]>;
};

export function getTopicParagraphMapping(
  aiResponse: { topicParagraphMapping?: unknown } | null | undefined,
): TopicParagraphMapping | null {
  const raw = aiResponse?.topicParagraphMapping;
  if (!raw || typeof raw !== 'object') return null;
  const m = raw as Partial<TopicParagraphMapping>;
  if (
    m.version !== TOPIC_PARAGRAPH_MAPPING_VERSION ||
    typeof m.topicsHash !== 'string' ||
    typeof m.transcriptHash !== 'string' ||
    !m.byTopic ||
    typeof m.byTopic !== 'object'
  ) {
    return null;
  }
  return m as TopicParagraphMapping;
}

/**
 * Normalize a story's `ai_response` field — which can be a JSON string, a
 * plain object, or unset — into a flat `Record<string, unknown>` suitable
 * for merging additional keys into. Invalid JSON is logged and treated as
 * empty so a malformed blob never blocks a write.
 *
 * Pass the raw field value (typically `record.ai_response` from a DatoCMS
 * fetch). `storyId` is optional and only used to enrich the warn log.
 */
export function parseAiResponseBlob(
  raw: unknown,
  storyId?: string,
): Record<string, unknown> {
  if (typeof raw === 'string' && raw.length > 0) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return parsed as Record<string, unknown>;
      }
    } catch (e) {
      logger.warn('ai_response was not valid JSON; overwriting', {
        storyId,
        error: extractErrorInfo(e),
      });
    }
    return {};
  }
  if (raw && typeof raw === 'object') {
    return { ...(raw as Record<string, unknown>) };
  }
  return {};
}

/**
 * Resolve the best available thumbnail URL for a dashboard story.
 * Mirrors the priority chain used across Dashboard, Home, and Folder views.
 */
export function resolveDashboardStoryThumbnailUrl(
  story: DashboardStory,
): string | undefined {
  if (story.thumbnail?.url) return story.thumbnail.url;
  if (story.selfHostedVideo?.video?.thumbnailUrl)
    return story.selfHostedVideo.video.thumbnailUrl;
  if (story.finalVideo?.thumbnail?.url) return story.finalVideo.thumbnail.url;
  const finalThumb = story.finalVideo?.videoFilePrimary?.video?.thumbnailUrl;
  if (finalThumb) return finalThumb;
  if (story.originalVideo?.video?.thumbnailUrl)
    return story.originalVideo.video.thumbnailUrl;
  // Last resort: derive a real frame straight from Mux when no stored thumbnail
  // URL exists (e.g. AWS/self-hosted stories whose signed thumbnail hasn't
  // loaded, or videos still processing). The dashboard query selects these
  // playback ids, so this keeps every story showing an actual video frame.
  const muxPlaybackId =
    story.finalVideo?.videoFilePrimary?.video?.muxPlaybackId ||
    story.originalVideo?.video?.muxPlaybackId;
  if (muxPlaybackId) return getMuxHostedThumbnailUrl(muxPlaybackId);
  return undefined;
}

/**
 * Format a duration value (seconds, possibly numeric string) as MM:SS,
 * or undefined when the value is missing/zero/non-finite.
 */
function formatDurationLabel(seconds?: number | string): string | undefined {
  if (seconds == null) return undefined;
  const total = typeof seconds === 'string' ? Number(seconds) : seconds;
  if (!Number.isFinite(total) || total <= 0) return undefined;
  const m = Math.floor(total / 60);
  const s = Math.floor(total % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Get the best available duration label (MM:SS) for a dashboard story,
 * falling back through finalVideo / selfHostedVideo / originalVideo.
 */
export function getDashboardStoryDurationLabel(
  story: DashboardStory,
): string | undefined {
  return (
    formatDurationLabel(story.finalVideo?.videoFilePrimary?.video?.duration) ??
    formatDurationLabel(story.selfHostedVideo?.video?.duration) ??
    formatDurationLabel(story.originalVideo?.video?.duration)
  );
}

// Per-story content output counts, shown alongside the clip count in the
// Story Manager rows. Pull quotes are an array; articles (blogs) and social
// posts are timestamp-keyed maps.
export function getPullQuotesCount(story: DashboardStory): number {
  return story.shareableImages?.length ?? 0;
}

export function getArticlesCount(story: DashboardStory): number {
  return story.savedBlog ? Object.keys(story.savedBlog).length : 0;
}

export function getSocialPostsCount(story: DashboardStory): number {
  return story.savedSocialPost ? Object.keys(story.savedSocialPost).length : 0;
}
